import { useState } from 'preact/hooks';
import type { PrDetailBody } from '../../shared/pr/api';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiSend } from '../lib/api';

// The petty cash route on a request (D65). Procurement chooses it in Sourcing, makes the claim
// from the request or goes back to an RFQ; the requester only sees that Procurement is buying it
// and confirms they received it. The broker checks every step again.

type Action = 'choose' | 'backToRfq' | 'received';

export function PettyCashRoute({ pr, onDone }: { pr: PrDetailBody; onDone: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ text: string; ref: string | null } | null>(null);
  const pc = pr.pettyCash;
  if (!pc) return null;

  async function act(action: Action) {
    setBusy(true);
    setProblem(null);
    try {
      await apiSend('POST', `/api/prs/${encodeURIComponent(pr.prNo)}/petty-cash`, {
        action,
        etag: pr.etag,
      });
      onDone();
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setProblem({
        text:
          e?.code === 'claim_in_progress'
            ? t('pcr.err.inProgress')
            : e?.code === 'conflict' || e?.code === 'invalid_transition'
              ? t('pc.err.conflict')
              : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  const error = problem && (
    <div class="form-problem" role="alert">
      <p>{problem.text}</p>
      {problem.ref && (
        <p class="caption" dir="ltr">
          {t('error.reference', { ref: problem.ref })}
        </p>
      )}
    </div>
  );

  if (pr.status === 'Sourcing') {
    if (!pc.canChoose) return null;
    return (
      <div class="stack">
        {error}
        <div class="actions">
          <button type="button" class="button" disabled={busy} onClick={() => void act('choose')}>
            {t('pcr.choose')}
          </button>
        </div>
        <p class="caption">{t('pcr.chooseHint')}</p>
      </div>
    );
  }

  const staff = pc.canClaim || pc.canBackToRfq || !!pc.pcNo;
  return (
    <section class="card stack" aria-labelledby="pcr-title">
      <h3 id="pcr-title" class="card__title">
        {staff ? t('pcr.titleStaff') : t('pcr.titleRequester')}
      </h3>
      {error}
      <p>{staff ? t('pcr.staffText') : t('pcr.requesterText')}</p>
      {pc.received && <p class="caption">{t('pcr.receivedDone')}</p>}
      {pc.pcNo && (
        <p class="caption">
          {t('pcr.claim')}{' '}
          <a href={`#/petty-cash/${pc.pcNo}`} dir="ltr">
            {pc.pcNo}
          </a>
          {pc.claimStatus && ` · ${t(`pc.status.${pc.claimStatus}` as MessageKey)}`}
        </p>
      )}
      <div class="actions">
        {pc.canConfirmReceived && (
          <button
            type="button"
            class="button button--primary"
            disabled={busy}
            onClick={() => void act('received')}
          >
            {staff ? t('pcr.receivedOnBehalf') : t('pcr.received')}
          </button>
        )}
        {pc.canClaim && (
          <a class="button button--primary" href={`#/requests/${pr.prNo}/petty-cash`}>
            {t('pc.makeClaim')}
          </a>
        )}
        {pc.canBackToRfq && (
          <button
            type="button"
            class="button"
            disabled={busy}
            onClick={() => void act('backToRfq')}
          >
            {t('pcr.backToRfq')}
          </button>
        )}
      </div>
    </section>
  );
}
